/**
 * Turkish labels for admin event projection kinds. Mirrors the
 * mockup `design-reference/js/admin-store.jsx` activity dictionary
 * so the dashboard icon-led list and recent-activity page agree on
 * what each `kind` string means in operator language.
 */
import { Icon } from '../ui/Icon.js';

export const ACTIVITY_KIND_LABEL: Record<string, string> = {
  maintenance_toggled: 'Bakım modu güncellendi',
  user_banned: 'Kullanıcı banlandı',
  user_unbanned: 'Kullanıcı banı kaldırıldı',
  user_soft_deleted: 'Kullanıcı silindi',
  balance_adjusted: 'Bakiye düzenlendi',
  order_delivered: 'Sipariş teslim edildi',
  order_delivery_failed: 'Sipariş teslim başarısız',
  provider_degraded: 'Sağlayıcı bozuldu',
  provider_healed: 'Sağlayıcı düzeldi',
  renewal_completed: 'Yenileme tamamlandı',
  renewal_failed: 'Yenileme başarısız',
  session_degraded: 'Oturum bozuldu',
  upstream_auth_challenge: 'Upstream auth challenge',
};

export type ActivityKindTone = 'neutral' | 'success' | 'warn' | 'danger';

export function toneForActivityKind(kind: string): ActivityKindTone {
  if (kind.includes('failed') || kind.includes('degraded') || kind.includes('challenge')) return 'danger';
  if (kind.includes('delivered') || kind.includes('healed') || kind.includes('completed')) return 'success';
  if (kind.includes('toggled') || kind.includes('banned') || kind.includes('deleted') || kind.includes('adjusted')) return 'warn';
  return 'neutral';
}

export function iconForActivityKind(kind: string): React.FC<{ size?: number }> {
  if (kind.startsWith('maintenance')) return Icon.Settings;
  if (kind.startsWith('user_ban') || kind.startsWith('user_unban')) return Icon.User;
  if (kind === 'user_soft_deleted') return Icon.Trash;
  if (kind === 'balance_adjusted') return Icon.Coins;
  if (kind.startsWith('order_deliver')) return Icon.Inbox;
  if (kind.startsWith('provider_')) return Icon.Globe;
  if (kind.startsWith('renewal_')) return Icon.Refresh;
  if (kind.startsWith('session_')) return Icon.Lock;
  if (kind === 'upstream_auth_challenge') return Icon.Shield;
  return Icon.Activity;
}

/** Friendly relative-time formatter for activity timestamps. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return `${diffSec} sn önce`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} g önce`;
  try {
    return new Date(iso).toLocaleDateString('tr-TR');
  } catch {
    return iso;
  }
}
